import {
  fetchCart,
  fetchUpdateItem,
  fetchAddItem,
  fetchRemoveItem,
  fetchSwapSellingPlan,
  fetchProductByHandle,
  applyDiscountLineItem,
  clearDiscountCode,
} from "@/services/cart";
import {triggerEvent} from "@/utils/custom-events";

/**
 * The cart applies every change on screen straight away and sends the requests
 * behind it. Before this, each action opened with `if (state.loading) return`
 * and awaited two round trips — the write and the `/cart.js` that followed — so
 * the second tap on a stepper was dropped and the row sat disabled in between.
 *
 * What that costs, and how it is paid for:
 *
 *   - Requests must still reach Shopify one at a time, or two updates to the
 *     same line race and the loser wins. `queueCartMutation` chains them.
 *   - `/cart/add.js` gives a line a key only once it has been created, and so
 *     does a selling-plan change. Until then the line is on screen under an
 *     optimistic key, so `keyAliases` maps that to the real one and queued
 *     requests resolve it when they run — the buyer can keep pressing + on a
 *     line whose add is still in flight.
 *   - Totals are recomputed locally from the lines. Cart-level discounts are
 *     not in the line prices, so the last server totals are kept and applied
 *     on top; otherwise a discount would vanish until the server answered.
 *
 * An optimistic line has to carry every field the row reads, or the row shows a
 * gap until the server answers. What `CartItem` and the two cart views read
 * today: price, final_price, original_price, compare_at_price, line_price,
 * final_line_price, original_line_price, total_discount, quantity, key, id,
 * variant_id, product_id, title, url, handle, image, featured_image.url,
 * featured_image.alt, and selling_plan_allocation (with selling_plan.id/.name).
 * `subtotalCompareAt` in the drawer needs compare_at_price and
 * original_line_price in particular, or the strike-through total lags.
 */

const OPTIMISTIC_KEY_PREFIX = "__opt_";

let mutationChain = Promise.resolve();
let pendingMutations = 0;
// Bumped by every queued mutation, so a background refresh can tell that the
// cart changed while it was in flight.
let cartVersion = 0;
// Background refreshes: only the latest one may report. One skipped because
// changes were still queued is simply dropped — whichever action drains the
// queue asks for its own refresh afterwards, so the final state still arrives.
let refreshSeq = 0;

const keyAliases = new Map();

const resolveKey = key => {
  let current = key;
  const seen = new Set();
  while (keyAliases.has(current) && !seen.has(current)) {
    seen.add(current);
    current = keyAliases.get(current);
  }
  return current;
};

const isOptimisticKey = key =>
  typeof key === "string" && key.startsWith(OPTIMISTIC_KEY_PREFIX);

const buildOptimisticKey = variantId =>
  `${OPTIMISTIC_KEY_PREFIX}${variantId}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const findLine = (state, key) => {
  const items = state.cartData?.items || [];
  return (
    items.find(i => i.key === key) || items.find(i => i.key === resolveKey(key))
  );
};

const planIdOf = line =>
  line?.selling_plan_allocation?.selling_plan?.id ?? null;

/**
 * The line an add would land on, or null for a new one. Shopify merges an add
 * into an existing line when the variant, the selling plan and the line
 * properties all match — so the preview has to do the same, or the product
 * shows twice until the refresh merges them.
 */
const findMergeTarget = (state, payload) => {
  const variantId = Number(payload.id);
  const planId = payload.selling_plan ?? null;
  const properties = JSON.stringify(payload.properties || {});

  return (state.cartData?.items || []).find(
    line =>
      Number(line.variant_id ?? line.id) === variantId &&
      String(planIdOf(line) ?? "") === String(planId ?? "") &&
      JSON.stringify(line.properties || {}) === properties,
  );
};

const queueCartMutation = (commit, task) => {
  cartVersion++;
  pendingMutations++;
  if (pendingMutations === 1) commit("cartLoading", true);

  const result = mutationChain.then(task, task);
  mutationChain = result.catch(() => {});

  return result.finally(() => {
    pendingMutations--;
    if (pendingMutations === 0) commit("cartLoading", false);
  });
};

// How the last cart Shopify sent got from its lines to its total.
let serverTotals = null;

const snapshotTotals = cart => {
  const linesTotal = (cart?.items || []).reduce(
    (sum, i) => sum + (Number(i.final_line_price) || 0),
    0,
  );
  const discounts = (cart?.cart_level_discount_applications || []).map(d => ({
    percentage: d.value_type === "percentage",
    amount: Number(d.total_allocated_amount) || 0,
  }));
  const discount = discounts.reduce((sum, d) => sum + d.amount, 0);
  return {
    linesTotal,
    discounts,
    // Anything else between lines and total, kept as it was.
    residual: (Number(cart?.total_price) || 0) - (linesTotal - discount),
  };
};

const recomputeTotals = cart => {
  if (!cart || !Array.isArray(cart.items)) return;

  let count = 0;
  let subtotal = 0;
  let total = 0;
  for (const item of cart.items) {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const finalPrice = Number(item.final_price ?? price);
    count += qty;
    subtotal += price * qty;
    total += finalPrice * qty;
    item.line_price = price * qty;
    item.original_line_price = price * qty;
    item.final_line_price = finalPrice * qty;
  }

  // Percentage discounts scale with the lines, fixed amounts stay (capped at
  // the lines).
  if (serverTotals && cart.items.length) {
    const ratio =
      serverTotals.linesTotal > 0 ? total / serverTotals.linesTotal : 1;
    const discount = serverTotals.discounts.reduce(
      (sum, d) => sum + (d.percentage ? d.amount * ratio : d.amount),
      0,
    );
    total = Math.max(
      0,
      Math.round(total - Math.min(discount, total) + serverTotals.residual),
    );
  }

  cart.item_count = count;
  cart.items_subtotal_price = subtotal;
  cart.total_price = total;
};

/**
 * The `selling_plan_allocation` Shopify would send for `planId`. The plan
 * object from the product JSON is used whole rather than a minimal stand-in,
 * because a row may read anything on it — `price_adjustments` among others.
 */
const buildAllocation = (product, planId, price, compareAtPrice) => {
  const plan = product?.selling_plan_groups
    ?.flatMap(group => group.selling_plans)
    .find(p => p.id == planId);

  return {
    selling_plan: plan || {id: planId, name: "", price_adjustments: []},
    price,
    compare_at_price: compareAtPrice,
    per_delivery_price: price,
    price_adjustments: [],
  };
};

/**
 * The line to show for an add that has not reached Shopify yet, from the
 * product JSON already loaded for the cart or from an `_optimistic` override on
 * the payload (`{product_title, variant_title, title, price, compare_at_price,
 * image, handle}`) for a caller that knows the product without it being cached
 * — which is every add of something not already in the cart.
 *
 * Returns null when neither is available: a line with no title and no price is
 * worse than the row appearing a moment later. The rest of the add stays
 * non-blocking either way.
 */
const buildOptimisticLine = (payload, productsCache) => {
  const variantId = Number(payload.id);
  const quantity = Number(payload.quantity) || 1;
  const override = payload._optimistic || {};

  const cachedProduct = productsCache?.[variantId];
  const variant = cachedProduct?.variants?.find(v => v.id == variantId);

  const productTitle = override.product_title || cachedProduct?.title || "";
  const variantTitleRaw =
    override.variant_title || variant?.title || variant?.public_title || "";
  const variantTitle =
    variantTitleRaw === "Default Title" ? "" : variantTitleRaw;
  const title =
    override.title ||
    (variantTitle ? `${productTitle} - ${variantTitle}` : productTitle);
  const handle = override.handle || cachedProduct?.handle || "";
  // `nil | img_url` in Liquid hands back the admin's "no image" placeholder,
  // which is a non-empty string — so a caller passing a variant image has to
  // have checked the variant actually has one.
  const image =
    override.image ||
    variant?.featured_image?.src ||
    variant?.featured_image ||
    cachedProduct?.featured_image ||
    cachedProduct?.images?.[0] ||
    "";

  const price = Number(
    override.price ?? variant?.price ?? cachedProduct?.price ?? NaN,
  );
  const compareAt =
    Number(
      override.compare_at_price ??
        variant?.compare_at_price ??
        cachedProduct?.compare_at_price,
    ) || null;

  if (!title || !Number.isFinite(price)) return null;

  const line = {
    key: buildOptimisticKey(variantId),
    id: variantId,
    variant_id: variantId,
    // Coerced: the row matches it with `===` against the ids in
    // `products_subscribe`, so a serializer that quotes the id would never
    // match.
    product_id: Number(override.product_id ?? cachedProduct?.id) || null,
    handle,
    url: handle
      ? `/products/${handle}${variantId ? `?variant=${variantId}` : ""}`
      : "#",
    title,
    product_title: productTitle || title,
    variant_title: variantTitle,
    quantity,
    price,
    final_price: price,
    original_price: price,
    // The drawer's strike-through total reads this off the line, so without it
    // the saving on a just-added product only shows up on the refresh.
    compare_at_price: compareAt,
    line_price: price * quantity,
    final_line_price: price * quantity,
    original_line_price: (compareAt || price) * quantity,
    total_discount: 0,
    discounts: [],
    line_level_discount_allocations: [],
    image,
    featured_image: {url: image, src: image, alt: title},
    properties: payload.properties || {},
    _optimistic: true,
  };

  // Shopify omits this key entirely on a one-time line, and a row that keys its
  // subscription block off `"selling_plan_allocation" in item` would take a key
  // holding `null` as a subscription.
  if (payload.selling_plan) {
    line.selling_plan_allocation = buildAllocation(
      cachedProduct,
      payload.selling_plan,
      price,
      compareAt || price,
    );
  }

  return line;
};

// What a line costs on `planId` (null = one-time), so the price moves the
// moment the buyer switches. Falls back to the current price while the product
// JSON is still loading.
const optimisticPlan = (product, line, planId) => {
  const variant = product?.variants?.find(v => v.id == line.id);
  const allocation = planId
    ? variant?.selling_plan_allocations?.find(a => a.selling_plan_id == planId)
    : null;
  const price = Number(
    (planId ? allocation?.price : variant?.price) ?? line.price,
  );
  const discountShare = line.price > 0 ? line.final_price / line.price : 1;

  if (!planId) {
    return {allocation: null, price, finalPrice: price * discountShare};
  }

  return {
    allocation: buildAllocation(
      product,
      planId,
      price,
      Number(allocation?.compare_at_price ?? line.compare_at_price ?? line.price),
    ),
    price,
    finalPrice: price * discountShare,
  };
};

// Carries each line's row identity across a server refresh. A line that got a
// new key keeps the key it was rendered with, so the row is updated in place
// instead of being remounted.
const carryViewKeys = (prevItems, nextItems) => {
  const viewKeys = new Map();
  for (const item of prevItems || []) {
    viewKeys.set(resolveKey(item.key), item.viewKey || item.key);
  }

  const used = new Set();
  for (const item of nextItems || []) {
    const viewKey = viewKeys.get(item.key);
    item.viewKey = viewKey && !used.has(viewKey) ? viewKey : item.key;
    used.add(item.viewKey);
  }
};

const ensureCart = state => {
  if (!state.cartData) {
    state.cartData = {
      items: [],
      item_count: 0,
      total_price: 0,
      items_subtotal_price: 0,
      total_discount: 0,
      note: "",
    };
  }
  if (!Array.isArray(state.cartData.items)) state.cartData.items = [];
};

/**
 * state
 */
const state = {
  visible: false,
  cartData: false,
  // A gift card (or any code Shopify only resolves at checkout) never shows up
  // in /cart.js, so it is tracked here instead of inside cartData — which
  // initCart replaces wholesale on every cart fetch.
  pendingDiscountCode: "",
  loading: false,
  productsWithDetails: {},
};

/**
 * getters
 */
const getters = {
  cartCount: state => (state.cartData ? state.cartData.item_count : 0),
};

/**
 * mutations
 */
const mutations = {
  TOGGLE(state) {
    state.visible = !state.visible;
  },

  SHOW(state) {
    state.visible = true;
  },

  HIDE(state) {
    state.visible = false;
  },

  cartLoading(state, value) {
    state.loading = value;
  },

  initCart(state, payload) {
    // An optimistic line whose add already went through is in the payload
    // under its real key; keeping it too would show the product twice.
    const optimisticItems =
      state.cartData?.items?.filter(
        i => i._optimistic && !keyAliases.has(i.key),
      ) || [];

    carryViewKeys(state.cartData?.items, payload?.items);
    if (payload) serverTotals = snapshotTotals(payload);

    if (pendingMutations > 0 && optimisticItems.length && payload) {
      const merged = {
        ...payload,
        items: [...optimisticItems, ...(payload.items || [])],
      };
      recomputeTotals(merged);
      state.cartData = merged;
      return;
    }

    state.cartData = payload;
  },

  pushOptimisticItem(state, item) {
    ensureCart(state);
    state.cartData.items.unshift(item);
    recomputeTotals(state.cartData);
  },

  removeItemByKey(state, key) {
    if (!state.cartData) return;
    const idx = state.cartData.items.indexOf(findLine(state, key));
    if (idx !== -1) {
      state.cartData.items.splice(idx, 1);
      recomputeTotals(state.cartData);
    }
  },

  restoreItem(state, {item, index}) {
    if (!state.cartData) return;
    const safeIndex = Math.max(0, Math.min(index, state.cartData.items.length));
    state.cartData.items.splice(safeIndex, 0, item);
    recomputeTotals(state.cartData);
  },

  setItemQuantity(state, {key, quantity}) {
    const item = findLine(state, key);
    if (!item) return;
    item.quantity = quantity;
    recomputeTotals(state.cartData);
  },

  setItemPlan(state, {key, allocation, price, finalPrice}) {
    const item = findLine(state, key);
    if (!item) return;
    if (allocation) {
      item.selling_plan_allocation = allocation;
    } else {
      delete item.selling_plan_allocation;
    }
    if (price != null) {
      item.price = price;
      item.final_price = finalPrice ?? price;
      recomputeTotals(state.cartData);
    }
  },

  setPendingDiscountCode(state, payload) {
    state.pendingDiscountCode = payload || "";
  },

  setProductDetails(state, {variantId, product}) {
    state.productsWithDetails = {
      ...state.productsWithDetails,
      [variantId]: product,
    };
  },
};

/**
 * actions
 */
const actions = {
  toggle({commit}) {
    commit("TOGGLE");
  },

  show({commit}) {
    commit("SHOW");
  },

  hide({commit}) {
    commit("HIDE");
  },

  setCode({commit}, payload) {
    commit("setPendingDiscountCode", payload);
  },

  // `background` is the refresh that follows the buyer's own changes. If the
  // cart changed again while it was in flight, its snapshot is already stale
  // and would briefly undo that change on screen, so it is dropped: the
  // refresh after the newer change reports the final state.
  async initCart({commit, dispatch}, {background = false} = {}) {
    const version = cartVersion;
    const seq = background ? ++refreshSeq : refreshSeq;

    try {
      const data = await fetchCart();

      if (background) {
        // A newer refresh is already on its way.
        if (seq !== refreshSeq) return data;
        if (pendingMutations > 0) return data;
        // The change that made this stale has already drained: fetch again.
        if (version !== cartVersion) {
          return dispatch("initCart", {background: true});
        }
      }

      commit("initCart", data);

      if (data?.items?.length) {
        await Promise.allSettled(
          data.items.map(item => dispatch("loadProductDetails", item)),
        );
      }

      return data;
    } catch (error) {
      console.error("Init cart error:", error);
      throw error;
    }
  },

  onCartModified: ({state}, payload) => {
    let modifiedCartMsg;
    const itemCount = state.cartData?.item_count ?? 0;

    switch (payload) {
      case "REMOVED":
        modifiedCartMsg = `Product removed from cart. You now have ${itemCount} items in your cart.`;
        break;
      case "UPDATED":
        modifiedCartMsg = `Product changed in cart. You now have ${itemCount} items in your cart.`;
        break;
      case "ADDED":
      default:
        modifiedCartMsg = `Product added to cart. You now have ${itemCount} items in your cart.`;
    }

    const srCartAlert = document.getElementById("sr-cart-alert");
    if (srCartAlert) {
      const newItem = document.createElement("span");
      newItem.textContent = modifiedCartMsg;
      srCartAlert.appendChild(newItem);
    }
  },

  removeItem: async ({commit, state, dispatch}, payload) => {
    const items = Array.isArray(payload) ? payload : [payload];
    if (!items.length) return;

    const snapshots = items
      .map(item => {
        const line = findLine(state, item.key);
        if (!line) return null;
        return {item: line, index: state.cartData.items.indexOf(line)};
      })
      .filter(Boolean);

    for (const snapshot of snapshots) {
      commit("removeItemByKey", snapshot.item.key);
    }
    dispatch("onCartModified", "REMOVED");

    try {
      await queueCartMutation(commit, () => {
        // Keys are resolved only now, so a line removed while its add was
        // still in flight is removed by its real key.
        const keys = items
          .map(item => resolveKey(item.key))
          .filter(key => !isOptimisticKey(key));
        if (!keys.length) return null;
        return fetchRemoveItem(keys);
      });
      if (pendingMutations === 0) dispatch("initCart", {background: true});
    } catch (error) {
      console.error(error.message);
      for (const snapshot of [...snapshots].reverse()) {
        commit("restoreItem", snapshot);
      }
      dispatch("initCart", {background: true});
      throw error;
    }
  },

  addItem: async ({commit, state, dispatch}, payload) => {
    const rawItems = Array.isArray(payload) ? payload : [payload];

    const optimisticLines = [];
    const mergedLines = [];

    for (const item of rawItems) {
      const existing = findMergeTarget(state, item);

      if (existing) {
        mergedLines.push({key: existing.key, quantity: existing.quantity});
        commit("setItemQuantity", {
          key: existing.key,
          quantity: existing.quantity + (Number(item.quantity) || 1),
        });
        continue;
      }

      const line = buildOptimisticLine(item, state.productsWithDetails);
      if (!line) continue;

      optimisticLines.push(line);
      commit("pushOptimisticItem", line);
      if (line.handle && !state.productsWithDetails[line.id]) {
        dispatch("loadProductDetails", line);
      }
    }

    dispatch("onCartModified", "ADDED");
    dispatch("show");
    // Asked for, not inferred. `Cart` opens the drawer from a `watch` on
    // `visible`, which only fires when the value *changes* — so an add while
    // `visible` was already true opened nothing. Triggering it here makes the
    // drawer open on every add regardless of the previous state, and `open()`
    // on an already-open modal is a no-op.
    //
    // Deliberately here and not inside the `show` action: `Modal.open()` calls
    // back into `show`, so triggering from there would loop.
    triggerEvent("modal:cart:open");

    // Rebuilt rather than forwarded: `_optimistic` is for drawing the row and
    // must never reach Shopify.
    const apiItems = rawItems.map(item => {
      const out = {id: item.id, quantity: Number(item.quantity) || 1};
      if (item.selling_plan) out.selling_plan = item.selling_plan;
      if (item.properties) out.properties = item.properties;
      return out;
    });

    try {
      await queueCartMutation(commit, async () => {
        const data = await fetchAddItem(apiItems);
        // Inside the task, so requests queued behind the add already see the
        // real keys when they run. Only paired up when every line was shown
        // optimistically — otherwise the indexes do not line up.
        if (optimisticLines.length === rawItems.length) {
          (data?.items || []).forEach((real, i) => {
            if (real?.key && optimisticLines[i]) {
              keyAliases.set(optimisticLines[i].key, real.key);
            }
          });
        }
        return data;
      });

      // Always refresh when nothing was shown up front: that request is what
      // puts the line on screen.
      if (
        pendingMutations === 0 ||
        (!optimisticLines.length && !mergedLines.length)
      ) {
        dispatch("initCart", {background: true});
      }
    } catch (error) {
      console.error(error.message);
      for (const line of optimisticLines) {
        commit("removeItemByKey", line.key);
      }
      for (const line of mergedLines) {
        commit("setItemQuantity", line);
      }
      dispatch("initCart", {background: true});
      throw error;
    }
  },

  updateItem: async ({commit, dispatch}, payload) => {
    const {item, type} = payload;

    let quantity;
    if (type === "increment") quantity = item.quantity + 1;
    else if (type === "decrement") quantity = item.quantity - 1;
    else if (type === "update") quantity = Number(payload.qty);
    else quantity = item.quantity;

    if (!Number.isFinite(quantity) || quantity < 0) return;
    // Taking the line to 0 removes it: drop the row now rather than showing a
    // 0-quantity line until the server answers.
    if (quantity === 0) return dispatch("removeItem", item);

    const previousQuantity = item.quantity;
    commit("setItemQuantity", {key: item.key, quantity});
    dispatch("onCartModified", "UPDATED");

    try {
      await queueCartMutation(commit, () => {
        const key = resolveKey(item.key);
        // The add for this line failed: there is nothing to update.
        if (isOptimisticKey(key)) return null;
        return fetchUpdateItem({key, quantity});
      });
      if (pendingMutations === 0) dispatch("initCart", {background: true});
    } catch (error) {
      console.error(error.message);
      commit("setItemQuantity", {key: item.key, quantity: previousQuantity});
      dispatch("initCart", {background: true});
      throw error;
    }
  },

  updatePlan: async ({commit, state, dispatch}, {planId, item}) => {
    const cartItem = findLine(state, item.key);
    if (!cartItem) return;

    const previous = {
      allocation: cartItem.selling_plan_allocation || null,
      price: cartItem.price,
      finalPrice: cartItem.final_price,
    };
    commit("setItemPlan", {
      key: item.key,
      ...optimisticPlan(
        state.productsWithDetails[cartItem.id],
        cartItem,
        planId,
      ),
    });
    dispatch("onCartModified", "UPDATED");

    try {
      await queueCartMutation(commit, async () => {
        const key = resolveKey(item.key);
        // The add for this line failed: there is nothing to change.
        if (isOptimisticKey(key)) return;

        const cart = await fetchSwapSellingPlan({
          key,
          quantity: findLine(state, item.key)?.quantity ?? item.quantity,
          planId,
        });

        // The line comes back under a new key; point the old one at it so
        // requests queued behind this one reach the right line.
        const otherKeys = new Set(
          (state.cartData?.items || [])
            .filter(i => i !== findLine(state, item.key))
            .map(i => resolveKey(i.key)),
        );
        const matches = (cart?.items || []).filter(
          i =>
            i.variant_id == (cartItem.variant_id || cartItem.id) &&
            planIdOf(i) == (planId || null),
        );
        const changed = matches.find(i => !otherKeys.has(i.key)) || matches[0];
        if (changed && changed.key !== key) keyAliases.set(key, changed.key);
      });

      if (pendingMutations === 0) dispatch("initCart", {background: true});
    } catch (error) {
      console.error("Update plan error:", error);
      commit("setItemPlan", {key: item.key, ...previous});
      dispatch("initCart", {background: true});
      throw error;
    }
  },

  loadProductDetails: async ({commit, state}, cartItem) => {
    if (state.productsWithDetails[cartItem.id]) return;

    try {
      const product = await fetchProductByHandle(cartItem.handle);
      if (!product) return;

      commit("setProductDetails", {
        variantId: cartItem.id,
        product,
      });
    } catch (error) {
      console.error("Product details error", error);
    }
  },

  applyDiscount: async ({dispatch}, discountCode) => {
    try {
      await applyDiscountLineItem({discountCode});
      await dispatch("initCart", {background: true});
    } catch (error) {
      console.error("Apply discount error:", error);
      throw error;
    }
  },

  clearDiscount: async ({commit, dispatch}) => {
    try {
      await clearDiscountCode();
      commit("setPendingDiscountCode", "");
      await dispatch("initCart", {background: true});
    } catch (error) {
      console.error("Clear discount error:", error);
      throw error;
    }
  },
};

/**
 * export
 */
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
